import AutocompleteInput from '../InputComponents/AutocompleteInput';
import { MultiAutocompleteInput } from '../InputComponents/MultiAutocompleteInput';
import { NumericFieldInput } from '../InputComponents/NumericFieldInput';
import { TextFieldInput } from '../InputComponents/TextFieldInput';
import { ToggleSwitchInput } from '../InputComponents/ToggleSwitchInput';
import { ShowOptionCodeForProperties, EFieldSize, EPropertyType } from '../../data/Constants';
import { useTranslation } from 'react-i18next';

import type { IFormControlProps, IProperty, ILovDetail } from '../../../types';

/**
 * To render Enum property inputs in PDM tab
 * @param {IProperty} property is an object of product properties
 * @param {string | null} propValue is property value
 * @param {function} handleInputChange is a function to manage input change
 * @param {boolean} fieldsEnabled is a boolean flag
 * @param {function} t is an react function used for translation
 * @returns {React.JSX.Element} returns React element
 */

const renderEnumInput = (
  property: IProperty,
  propValue: string | null,
  handleInputChange: (code: string, value: string) => void,
  fieldsEnabled: boolean,
  t,
  i18n
): React.JSX.Element => {
  let formattedVal: ILovDetail | ILovDetail[] | null = null;
  if (property.listOfValues && property.listOfValues.length > 0 && propValue) {
    //Autocomplete for Properties section
    const val = JSON.parse(propValue); //Check the default value is array or not
    if (val.length > 0) {
      if (property.selectionType === EPropertyType.Multiple) {
        formattedVal = val.map((valLov: string) => {
          const match = property.listOfValues?.find((lov) => lov.code === valLov);
          return match ?? { code: 'NA', name: valLov }; // IF code not found in listOfValues, return the value as name with code 'NA' to avoid crash and highlight it in UI
        });
      } else if (property.selectionType === EPropertyType.Single) {
        const match = property.listOfValues?.find((lov) => lov.code === val[0]);
        formattedVal = match ?? { code: 'NA', name: val[0] }; // If code not found in listOfValues, return the value as name with code 'NA'
      }
    }
  }

  if (property.selectionType === EPropertyType.Multiple) {
    return (
      <MultiAutocompleteInput
        data={property.listOfValues || []}
        handleChange={handleInputChange}
        label={
          i18n.exists('labels.properties.' + property.code)
            ? t('labels.properties.' + property.code)
            : property.name + ' (' + property.code + ')'
        }
        disabled={!fieldsEnabled}
        value={formattedVal}
        code={property.code}
        disableClear={false}
        size={EFieldSize.Small}
        showCode={ShowOptionCodeForProperties.includes(property.code)}
      />
    );
  } else if (property.selectionType === EPropertyType.Single) {
    return (
      <AutocompleteInput
        data={property.listOfValues || []}
        handleChange={handleInputChange}
        label={
          i18n.exists('labels.properties.' + property.code)
            ? t('labels.properties.' + property.code)
            : property.name + ' (' + property.code + ')'
        }
        disabled={!fieldsEnabled}
        showCode={ShowOptionCodeForProperties.includes(property.code)}
        value={formattedVal}
        code={property.code}
        disableClear={false}
        size={EFieldSize.Small}
      />
    );
  }

  return <></>;
};

/**
 * returns form control based on property type
 * @param {IFormControlProps} props is used to send property details of product
 * @returns {React.JSX.Element} returns React element
 */

export const DynamicInputControl = (props: IFormControlProps): React.JSX.Element => {
  const { property, propValue, handleInputChange, fieldsEnabled } = props;
  const { t, i18n } = useTranslation();
  switch (property.type) {
    case EPropertyType.Boolean:
      return (
        <ToggleSwitchInput
          label={
            i18n.exists('labels.properties.' + property.code)
              ? t('labels.properties.' + property.code)
              : property.name + ' (' + property.code + ')'
          }
          disabled={!fieldsEnabled}
          handleInputChange={handleInputChange}
          value={propValue}
          code={property.code}
          size={EFieldSize.Medium}
        />
      );
    case EPropertyType.String:
      return (
        <TextFieldInput
          label={
            i18n.exists('labels.properties.' + property.code)
              ? t('labels.properties.' + property.code)
              : property.name + ' (' + property.code + ')'
          }
          handleInputChange={handleInputChange}
          disabled={!fieldsEnabled}
          value={propValue}
          code={property.code}
          size={EFieldSize.Small}
        />
      );
    case EPropertyType.Numeric:
      return (
        <NumericFieldInput
          label={
            i18n.exists('labels.properties.' + property.code)
              ? t('labels.properties.' + property.code)
              : property.name + ' (' + property.code + ')'
          }
          handleInputChange={handleInputChange}
          disabled={!fieldsEnabled}
          value={propValue}
          code={property.code}
          size={EFieldSize.Small}
        />
      );
    case EPropertyType.Enum:
      return renderEnumInput(property, propValue, handleInputChange, fieldsEnabled, t, i18n);
    default:
      return <></>;
  }
};
